import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import SEOHead from "@/components/SEOHead";
import EligibilityCheckForm from "@/components/EligibilityCheckForm";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Link } from "@/lib/router-compat";
import {
  Activity,
  Stethoscope,
  HeartPulse,
  Clock,
  ArrowRight,
  PhoneCall,
  CheckCircle2,
  HelpCircle,
} from "lucide-react";

export default function FindDoctorSpecializingInChronicPainPage() {
  const publishedDate = "2024-01-30";
  const updatedDate = "2026-11-03";
  const heroImage =
    "https://neurowellnesstms.com/wp-content/uploads/2024/01/how-can-i-find-a-doctor-who-specializes-in-chronic-pain.jpg";

  const faqs = [
    {
      q: "What credentials should I look for in a chronic pain specialist?",
      a: "Look for board certification in pain medicine, anesthesiology, neurology, or physical medicine & rehabilitation (PM&R), as well as clinical experience with interventional pain protocols like Ketamine infusion therapy.",
    },
    {
      q: "When should I transition from a primary care doctor to a pain specialist?",
      a: "If persistent pain lasts longer than 3 months and does not improve with conservative care, physical therapy, or standard analgesics, consulting a pain specialist is recommended.",
    },
    {
      q: "Do I need a referral to see a pain management doctor?",
      a: "Depending on your health insurance plan (HMO vs. PPO), a referral from your primary care physician may be required. Many private practices in Coral Springs also accept direct self-referrals.",
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="How Can I Find a Doctor Who Specializes in Chronic Pain? | Neuro Wellness"
        description="Learn how to find board-certified chronic pain specialists, interventional pain clinics, and advanced treatments like Ketamine infusions in Coral Springs, FL."
        canonical="/how-can-i-find-a-doctor-who-specializes-in-chronic-pain"
        keywords="find chronic pain doctor, chronic pain specialist Coral Springs, pain management physician, ketamine pain doctor, interventional pain clinic Florida"
        ogImage={heroImage}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "MedicalWebPage",
                "@id":
                  "https://neurowellnesstms.com/how-can-i-find-a-doctor-who-specializes-in-chronic-pain#webpage",
                name: "How Can I Find a Doctor Who Specializes in Chronic Pain?",
                url: "https://neurowellnesstms.com/how-can-i-find-a-doctor-who-specializes-in-chronic-pain",
                inLanguage: "en-US",
                datePublished: publishedDate,
                dateModified: updatedDate,
                description:
                  "A guide to finding qualified, board-certified chronic pain specialists and evaluating modern interventional pain management options.",
                publisher: {
                  "@type": "MedicalOrganization",
                  name: "Neuro Wellness TMS Centers of America",
                  url: "https://neurowellnesstms.com",
                  telephone: "+1-954-827-9793",
                  address: {
                    "@type": "PostalAddress",
                    streetAddress: "9734 West Sample Road, 2nd Floor, Suite B",
                    addressLocality: "Coral Springs",
                    addressRegion: "FL",
                    postalCode: "33065",
                    addressCountry: "US",
                  },
                },
              },
              {
                "@type": "FAQPage",
                "@id":
                  "https://neurowellnesstms.com/how-can-i-find-a-doctor-who-specializes-in-chronic-pain#faq",
                mainEntity: faqs.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
            ],
          }),
        }}
      />

      <Navbar />

      <main className="pt-16 sm:pt-20">
        {/* HERO */}
        <section className="relative overflow-hidden bg-primary-deep text-primary-foreground py-12 sm:py-16 lg:py-20">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-deep via-primary-deep/95 to-slate-900" />
          <div className="container-px relative mx-auto max-w-6xl">
            <div className="grid items-center gap-8 lg:grid-cols-12 lg:gap-12">
              <div className="order-2 lg:order-1 lg:col-span-7">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/15 px-3.5 py-1 text-xs font-semibold text-accent backdrop-blur-sm">
                    <Stethoscope className="h-4 w-4" />
                    Pain Management Specialist Guide
                  </span>
                  <span className="text-xs text-primary-foreground/75 flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    Medically Reviewed · Updated Jan 2024
                  </span>
                </div>

                <h1 className="font-headings text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight text-white">
                  How Can I Find a Doctor Who{" "}
                  <span className="text-accent">
                    Specializes in Chronic Pain?
                  </span>
                </h1>

                <p className="mt-5 text-base sm:text-lg text-primary-foreground/90 leading-relaxed max-w-2xl">
                  Finding the right pain specialist is critical for treating
                  complex neuropathic pain, CRPS, or fibromyalgia. Discover
                  step-by-step actions to locate board-certified experts and
                  interventional centers.
                </p>

                <div className="mt-6 flex flex-wrap gap-4 text-xs sm:text-sm text-primary-foreground/80">
                  <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-md backdrop-blur-sm border border-white/10">
                    <Activity className="h-4 w-4 text-accent" />
                    <span>Board-Certified Pain Physicians</span>
                  </div>
                  <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-md backdrop-blur-sm border border-white/10">
                    <HeartPulse className="h-4 w-4 text-accent" />
                    <span>Advanced Ketamine Protocols</span>
                  </div>
                </div>
              </div>

              <div className="order-1 lg:order-2 lg:col-span-5">
                <div className="rounded-2xl border border-white/15 bg-white p-5 sm:p-6 shadow-2xl text-foreground">
                  <div className="mb-4">
                    <span className="text-xs font-bold uppercase tracking-wider text-accent">
                      Pain Evaluation Inquiry
                    </span>
                    <h2 className="text-xl font-bold text-foreground mt-1">
                      Schedule a Pain Assessment
                    </h2>
                    <p className="text-xs text-muted-foreground mt-1">
                      Connect with our medical team in Coral Springs, FL to
                      discuss your chronic pain condition.
                    </p>
                  </div>
                  <EligibilityCheckForm
                    source="how-can-i-find-a-doctor-who-specializes-in-chronic-pain"
                    variant="card"
                    redirectTo="/thank-you-consultation"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* BREADCRUMBS */}
        <div className="container-px mx-auto max-w-4xl pt-6">
          <Breadcrumbs
            crumbs={[
              { name: "Resources", href: "/articles" },
              {
                name: "Find a Chronic Pain Doctor",
                href: "/how-can-i-find-a-doctor-who-specializes-in-chronic-pain",
              },
            ]}
          />
        </div>

        {/* MAIN ARTICLE BODY */}
        <article className="container-px mx-auto max-w-4xl py-6 space-y-12">
          <section className="space-y-4">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-foreground">
              Steps to Locate a Qualified Pain Specialist
            </h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <div className="border border-l-4 border-l-accent p-4 rounded-r-xl bg-card space-y-1">
                <h3 className="font-bold text-foreground text-base">
                  1. Primary Care & Specialist Referrals
                </h3>
                <p className="text-xs sm:text-sm">
                  Ask your primary care provider or neurologist for
                  recommendations to interventional pain management practices
                  specializing in your specific pain type.
                </p>
              </div>

              <div className="border border-l-4 border-l-accent p-4 rounded-r-xl bg-card space-y-1">
                <h3 className="font-bold text-foreground text-base">
                  2. Verify Credentials & Board Certification
                </h3>
                <p className="text-xs sm:text-sm">
                  Ensure the physician holds subspecialty board certification in
                  Pain Medicine from recognized medical boards (ABMS, ABA, AAN).
                </p>
              </div>

              <div className="border border-l-4 border-l-accent p-4 rounded-r-xl bg-card space-y-1">
                <h3 className="font-bold text-foreground text-base">
                  3. Evaluate Modern Interventional Options
                </h3>
                <p className="text-xs sm:text-sm">
                  Seek practices that offer modern non-opioid modalities, such
                  as intravenous Ketamine pain infusions, nerve blocks, and
                  neuromodulation.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ SECTION */}
          <section className="space-y-6 pt-6 border-t">
            <div className="flex items-center gap-2 text-accent">
              <HelpCircle className="h-6 w-6" />
              <h2 className="text-2xl font-bold text-foreground">
                Frequently Asked Questions
              </h2>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="rounded-xl border p-5 bg-card">
                  <h3 className="text-base font-bold text-foreground mb-2">
                    {faq.q}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {faq.a}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* RELATED PAGES */}
          <section className="space-y-4 pt-6 border-t">
            <h3 className="text-xl font-bold text-foreground">
              Related Resources
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link
                to="/ketamine-therapy-for-chronic-pain-a-powerful-solution"
                className="p-4 rounded-xl border bg-card hover:border-accent transition-colors flex items-center justify-between"
              >
                <div>
                  <h4 className="font-bold text-sm text-foreground">
                    Ketamine Therapy for Pain
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    NMDA antagonist infusions for chronic pain
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-accent" />
              </Link>
            </div>
          </section>

          {/* CTA BANNER */}
          <section className="rounded-2xl bg-gradient-to-br from-primary-deep to-slate-900 p-6 sm:p-8 text-white space-y-4">
            <h3 className="text-2xl font-bold">
              Consult With Our Pain Medical Team
            </h3>
            <p className="text-sm text-primary-foreground/90 leading-relaxed">
              At Neuro Wellness TMS Centers of America in Coral Springs, FL, our
              medical experts offer specialized interventional pain
              consultations.
            </p>
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <a
                href="tel:954-827-9793"
                className="inline-flex items-center gap-2 rounded-lg bg-accent px-5 py-2.5 text-sm font-semibold text-slate-950 hover:bg-accent/90 transition-colors"
              >
                <PhoneCall className="h-4 w-4" /> Call 954-827-9793
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/20 transition-colors"
              >
                Request Consultation
              </Link>
            </div>
          </section>
        </article>
      </main>

      <Footer />
    </div>
  );
}
